'use client';

import { ChangeEvent, useState } from 'react';

import { searchData } from '@/server/mutations/search.mutation';
import { debounce } from 'lodash';

import { Locale } from '@/i18n/config';
import { cn } from '@/utils/app.utils';

import { ISearchResult } from '@/types/app.type';

import Loading from '../Loading';
import NextIcon from '../NextIcon';
import Text from '../typography/Text';
import TextLink from '../typography/TextLink';
import { Input } from '../ui/Input';
import IconButton from '../buttons/IconButton';

type Props = {
  locale: Locale;
  tSearch: string;
  tNoResults: string;
  className?: string;
  inputClassName?: string;
};
const Search = ({
  locale,
  tSearch,
  tNoResults,
  className,
  inputClassName,
}: Props) => {
  const [search, setSearch] = useState('');
  const [searchResults, setSearchResults] = useState<ISearchResult[]>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const [openInput, setOpenInput] = useState<boolean>(false);

  const toggleOpenInput = () => setOpenInput(prev => !prev);

  const searchHandler = (e: ChangeEvent<HTMLInputElement>) => {
    const { value } = e.target;

    setSearch(value);

    const run = debounce(async (debouncedValue: string) => {
      if (debouncedValue.length > 3) {
        setLoading(true);
        try {
          const result = await searchData({
            search: debouncedValue,
            locale,
            tNoResults,
          });

          setSearchResults(result);
          setLoading(false);
        }
        catch (error) {
          console.log('error: ', error);
          setLoading(false);
        }
      }
      else {
        setSearchResults([]);
      }
    }, 1000);

    run(value);
  };

  const handleClick = () => {
    if (searchResults.length > 0) {
      setSearchResults([]);
      setSearch('');
    }
  };

  if (!openInput) {
    return (
      <IconButton onClick={toggleOpenInput}>
        <NextIcon size={20} src="/icons/search.svg" />
      </IconButton>
    );
  }

  return (
    <div
      className={cn(
        'absolute flex w-full top-0 left-0 right-0 flex-1 flex-row items-center justify-end bg-white dark:bg-neutral-900 py-8 z-50 border border-b-1 border-gray-300',
        className
      )}
    >
      <div className="relative w-full">
        <Input
          placeholder={tSearch}
          type="text"
          value={search}
          className={cn(
            'flex h-[50px] w-full border-none px-12 text-lg placeholder:text-muted-foreground focus-visible:ring-0 focus-visible:ring-transparent focus-visible:ring-offset-0 disabled:opacity-50',
            inputClassName
          )}
          onChange={searchHandler}
        />
        {loading && (
          <Loading
            className="h-4 w-4"
            rootClassName="absolute right-5 top-1/2 transform -translate-y-1/2"
          />
        )}
        <IconButton className="absolute top-0 right-4" onClick={toggleOpenInput}>
          <NextIcon size={20} src="/icons/x2.svg" />
        </IconButton>
      </div>
      {searchResults && searchResults.length > 0 && (
        <div className="absolute left-0 right-0 top-[100%] z-10 rounded-md border border-neutral-200 bg-white px-2 pb-3 pt-2 shadow-md dark:border-neutral-700 dark:bg-neutral-900">
          {searchResults.map((result, index) => (
            <div
              key={result.id + index}
              className="p-2"
              role="button" // div is not interactive, so we need to add role="button" to make it accessible
              tabIndex={0} // add tabIndex to make it focusable
              onClick={handleClick}
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  handleClick();
                }
              }}
            >
              {result.url ? (
                <TextLink className="font-medium" href={result.url}>
                  {result.title}
                </TextLink>
              ) : (
                <Text>{result.title}</Text>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default Search;
